import React, { useEffect, useState } from 'react';
import {
  VictoryLine,
  VictoryChart,
  VictoryAxis,
  VictoryTooltip,
  VictoryVoronoiContainer,
  VictoryLabel,
  VictoryLegend,
  VictoryArea
} from 'victory';
import { FaTimes } from 'react-icons/fa';
import { 
  getGrowthRate, 
  getComplexityMultiplier, 
  determineAuditRequirement,
  getForm990Cost,
  getStateRenewalCost
} from '../utils/projectionCalculations';

const CostChart = ({ data, onClose }) => {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: []
  });

  useEffect(() => {
    // Ensure we have valid data
    if (!data || !data.expectedRevenue) return;
    
    const revenue = parseFloat(data.expectedRevenue) || 0;
    const initialIndependentCost = parseFloat(data.independentCosts.initial) || 0;
    const ongoingIndependentCost = parseFloat(data.independentCosts.ongoing) || 0;
    const fiscalSponsorshipCost = parseFloat(data.fiscalSponsorshipCost) || 0;
    
    // Calculate 5-year projections with more realistic growth patterns
    const years = [1, 2, 3, 4, 5];
    
    // Create properly formatted data for Victory charts
    const independentData = [];
    const fiscalSponsorshipData = [];
    
    let cumulativeIndependent = 0;
    let cumulativeFiscal = 0;
    
    // More realistic growth factors
    const revenueGrowthRates = [1, 1.15, 1.25, 1.32, 1.38]; // Progressive growth
    const costInflationRates = [1, 1.04, 1.08, 1.13, 1.18]; // Compounding inflation
    const complexityFactors = [1, 1.05, 1.12, 1.18, 1.25]; // Increasing complexity
    
    years.forEach((year, index) => {
      // Calculate costs for this year with more realistic patterns
      let yearlyIndependentCost = 0;
      
      if (year === 1) {
        // First year includes setup costs
        yearlyIndependentCost = initialIndependentCost + ongoingIndependentCost;
      } else {
        // Subsequent years have increasing costs due to:
        // - Inflation
        // - Organizational complexity growth
        // - Regulatory requirements changes
        yearlyIndependentCost = ongoingIndependentCost * costInflationRates[index] * complexityFactors[index];
      }
      
      // Fiscal sponsorship costs grow with revenue
      const adjustedRevenue = revenue * revenueGrowthRates[index];
      const yearlyFiscalCost = (adjustedRevenue * 0.05) * costInflationRates[index];
      
      // Add to cumulative totals
      cumulativeIndependent += yearlyIndependentCost;
      cumulativeFiscal += yearlyFiscalCost;
      
      // Create data points in the format Victory expects
      independentData.push({ 
        x: year, 
        y: cumulativeIndependent,
        tooltip: `Year ${year}\nCumulative Cost: $${cumulativeIndependent.toLocaleString()}\nIncludes: ${year === 1 ? 'Initial setup and ' : ''}Annual compliance costs`
      });
      
      fiscalSponsorshipData.push({ 
        x: year, 
        y: cumulativeFiscal,
        revenue: adjustedRevenue,
        name: "fiscal-line"
      });
    });
    
    // Update chart data with properly formatted arrays
    setChartData({
      independentData,
      fiscalSponsorshipData,
      maxValue: Math.max(
        ...independentData.map(d => d.y),
        ...fiscalSponsorshipData.map(d => d.y)
      ) * 1.2 // Add 20% padding to the top
    });
  }, [data]);

  const customTheme = {
    axis: {
      style: {
        axis: { stroke: '#64748b' },
        grid: { stroke: 'rgba(100, 116, 139, 0.1)' },
        tickLabels: { fill: '#94a3b8', fontSize: 10 }
      }
    }
  };

  return (
    <div className="modal-overlay">
      <div className="cost-projection-modal">
        <div className="modal-header">
          <h3>5-Year Cost Projection</h3>
          <button className="close-button" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        <div className="modal-content">
          <div className="chart-container">
            <VictoryChart
              theme={customTheme}
              height={450}
              width={1200}
              domain={{ y: [0, chartData.maxValue || 1] }}
              padding={{ top: 80, bottom: 50, left: 100, right: 60 }}
              containerComponent={
                <VictoryVoronoiContainer
                  voronoiDimension="x"
                  labels={({ datum }) => {
                    // Only show tooltip for the specific line being hovered
                    if (datum.name === "fiscal-line") {
                      return `Year ${datum.x}\nProjected Revenue: $${datum.revenue.toLocaleString()}\nAdministrative Fee (5%): $${datum.y.toLocaleString()}\nIncludes all operational support, compliance, and infrastructure`;
                    } else if (datum.name === "independent-line") {
                      return datum.tooltip;
                    }
                    return null;
                  }}
                  labelComponent={
                    <VictoryTooltip
                      style={{
                        fontSize: 14,
                        fill: "white",
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
                      }}
                      flyoutStyle={{
                        stroke: 'rgba(255, 255, 255, 0.1)',
                        fill: 'rgba(0, 0, 0, 0.85)',
                        strokeWidth: 1,
                        filter: 'drop-shadow(0 0 10px rgba(0,0,0,0.5))'
                      }}
                      flyoutPadding={{ top: 20, bottom: 20, left: 25, right: 25 }}
                      cornerRadius={8}
                      dy={-20}
                      pointerLength={10}
                      pointerWidth={10}
                      constrainToVisibleArea={true}
                      flyoutWidth={450}
                    />
                  }
                />
              }
            >
              <defs>
                <linearGradient id="fiscalGradient" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#818cf8" stopOpacity={0.3}/>
                  <stop offset="100%" stopColor="#818cf8" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="independentGradient" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0%" stopColor="#ef4444" stopOpacity={0.3}/>
                  <stop offset="100%" stopColor="#ef4444" stopOpacity={0}/>
                </linearGradient>
              </defs>

              <VictoryLegend
                x={300}
                y={40}
                orientation="horizontal"
                gutter={50}
                style={{
                  labels: { fill: '#94a3b8', fontSize: 20 }
                }}
                data={[
                  { name: 'Fiscal Sponsorship (5%)', symbol: { fill: '#818cf8', size: 12 } },
                  { name: 'Independent 501(c)(3)', symbol: { fill: '#ef4444', size: 12 } }
                ]}
              />
              
              <VictoryAxis
                label="Years"
                tickValues={[1, 2, 3, 4, 5]}
                tickFormat={(t) => `Year ${t}`}
                style={{
                  axisLabel: { padding: 45, fill: '#94a3b8', fontSize: 20 },
                  tickLabels: { fontSize: 18, padding: 12, fill: '#94a3b8' },
                  grid: { stroke: 'rgba(100, 116, 139, 0.1)', strokeWidth: 1 }
                }}
              />
              
              <VictoryAxis
                dependentAxis
                label="Cost ($)"
                tickFormat={(t) => `$${(t / 1000).toFixed(0)}k`}
                style={{
                  axisLabel: { padding: 65, fill: '#94a3b8', fontSize: 20 },
                  tickLabels: { fontSize: 18, padding: 12, fill: '#94a3b8' },
                  grid: { stroke: 'rgba(100, 116, 139, 0.1)', strokeWidth: 1 }
                }}
              />

              <VictoryArea
                name="fiscal-area"
                data={chartData.fiscalSponsorshipData}
                style={{
                  data: { 
                    fill: "url(#fiscalGradient)",
                    fillOpacity: 0.3
                  }
                }}
                animate={{
                  duration: 2000,
                  onLoad: { duration: 1000 }
                }}
              />

              <VictoryArea
                name="independent-area"
                data={chartData.independentData}
                style={{
                  data: { 
                    fill: "url(#independentGradient)",
                    fillOpacity: 0.3
                  }
                }}
                animate={{
                  duration: 2000,
                  onLoad: { duration: 1000 }
                }}
              />

              <VictoryLine
                name="fiscal-line"
                data={chartData.fiscalSponsorshipData}
                style={{
                  data: { 
                    stroke: '#818cf8',
                    strokeWidth: 4,
                    strokeLinecap: "round"
                  }
                }}
                animate={{
                  duration: 2000,
                  onLoad: { duration: 1000 }
                }}
              />

              <VictoryLine
                name="independent-line"
                data={chartData.independentData}
                style={{
                  data: { 
                    stroke: '#ef4444',
                    strokeWidth: 4,
                    strokeLinecap: "round"
                  }
                }}
                animate={{
                  duration: 2000,
                  onLoad: { duration: 1000 }
                }}
              />
            </VictoryChart>
          </div>
          <div className="projection-notes">
            <div className="notes-grid">
              <div className="note-item">
                <div className="note-bullet"></div>
                <span style={{ fontSize: '1rem' }}>Fiscal Sponsorship: 5% fee covers all operational costs and compliance</span>
              </div>
              <div className="note-item">
                <div className="note-bullet"></div>
                <span style={{ fontSize: '1rem' }}>Independent 501(c)(3): Includes relevant IRS expenses, insurance, staffing, and state-specific costs</span>
              </div>
              <div className="note-item">
                <div className="note-bullet"></div>
                <span style={{ fontSize: '1rem' }}>Projections based on standard nonprofit growth patterns equally applied to both models</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.75);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          padding: 20px;
        }

        .cost-projection-modal {
          background-color: #1e293b;
          border-radius: 12px;
          width: 95%;
          max-width: 1200px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
        }

        .modal-header {
          padding: 15px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 1px solid rgba(148, 163, 184, 0.1);
        }

        .modal-header h3 {
          margin: 0;
          color: #94a3b8;
          font-size: 1.5rem;
          flex: 1;
          text-align: center;
        }

        .modal-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          padding: 20px;
          overflow: auto;
        }

        .chart-container {
          flex: 1;
          display: flex;
          justify-content: center;
          align-items: center;
          overflow: hidden;
          margin: 0 auto;
          width: 100%;
        }

        .projection-notes {
          width: 96%;
        }

        .notes-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          justify-content: space-between;
          align-items: center;
          padding: 0 -100px 20px;
          width: 100%;
          max-width: 1400px;
          margin: 0 auto;
        }

        .note-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          color: #94a3b8;
          white-space: normal;
          text-align: left;
          padding-right: 10px;
        }

        .note-bullet {
          min-width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #64748b;
        }

        .close-button {
          position: absolute;
          right: 20px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #94a3b8;
          cursor: pointer;
          padding: 5px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .close-button:hover {
          color: white;
        }

        @media (max-width: 768px) {
          .cost-projection-modal {
            width: 98%;
            max-height: 95vh;
          }
          
          .chart-container {
            transform: scale(0.8);
            transform-origin: center top;
          }
          
          .notes-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default CostChart; 